import { CreateEventFormData, CreateEventFormErrors, PublishType } from '@/types/admin/createEvent';
import { IEventPhoto } from '@/types/admin/events';
import { IEvent } from '@/types/admin/events';
import { useState, useCallback, useEffect } from 'react';
import { Platform } from 'react-native';
import { showConfirm } from '@/utils/common/alert';
import { composeValidators, requiredString, dateOrder, positiveIntegerOrZero, requireIf, validateData } from '@/utils/admin/validation';

// Convert IEvent to CreateEventFormData for editing
const convertEventToFormData = (event: IEvent): CreateEventFormData => {
  // Convert photos from backend format to unified format
  const photos: IEventPhoto[] = event.photos.map((photo, index) => ({
    id: photo.id,
    photoUrl: photo.photoUrl, // Keep existing photos with photoUrl
    eventId: photo.eventId,
    type: 'image/jpeg',
    name: `event_photo_${index}.jpg`,
  }));

  // Determine publish type based on event data
  let publishType: PublishType = 'publishNow';
  if (event.publishTime) {
    const publishDate = new Date(event.publishTime);
    const now = new Date();
    publishType = publishDate > now ? 'scheduled' : 'publishNow';
  }

  return {
    name: event.name || '',
    description: event.description || '',
    location: event.location || '',
    startTime: new Date(event.startTime),
    endTime: new Date(event.endTime),
    rsvpLimit: event.rsvpLimit?.toString() || '0',
    internalNotes: event.internalNotes || '',
    publishType,
    publishTime: event.publishTime ? new Date(event.publishTime) : new Date(),
    photos,
  };
};

export const useEditEventForm = (event: IEvent | null) => {
  const [formData, setFormData] = useState<CreateEventFormData>(() => {
    if (event) {
      return convertEventToFormData(event);
    }
    // Fallback to empty form if no event provided
    return {
      name: '',
      description: '',
      location: '',
      startTime: new Date(),
      endTime: new Date(),
      rsvpLimit: '0',
      internalNotes: '',
      publishType: 'publishNow',
      photos: [],
    };
  });
  
  const [errors, setErrors] = useState<CreateEventFormErrors>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Update form data when event changes
  useEffect(() => {
    if (event) {
      const newFormData = convertEventToFormData(event);
      setFormData(newFormData);
      setHasChanges(false);
    }
  }, [event]);

  const validateForm = useCallback((): boolean => {
    const validator = composeValidators<CreateEventFormData>(
      requiredString('name', 'Event name'),
      requiredString('description', 'Event description'),
      requiredString('location', 'Event location'),
      dateOrder('startTime', 'endTime', 'End time must be after start time'),
      positiveIntegerOrZero('rsvpLimit', 'RSVP limit'),
      requireIf((d) => d.publishType === 'scheduled', 'publishTime', 'Publish time'),
    );
    return validateData(formData, setErrors as any, validator);
  }, [formData]);

  const handleFieldChange = useCallback((field: keyof CreateEventFormData, value: string | Date | IEventPhoto[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
    
    // Clear error when user starts typing/changing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  }, [errors]);

  const handlePublishTypeChange = useCallback((type: PublishType) => {
    setFormData(prev => ({
      ...prev,
      publishType: type,
      publishTime: type === 'publishNow' ? new Date() : type === 'scheduled' ? new Date() : undefined,
    }));
    setHasChanges(true);
  }, []);

  const appendPhotos = useCallback((photosToAdd: IEventPhoto[]) => {
    setFormData(prev => ({
      ...prev,
      photos: [...prev.photos, ...photosToAdd],
    }));
    setHasChanges(true);
  }, []);

  const resetForm = useCallback(() => {
    if (event) {
      setFormData(convertEventToFormData(event));
      setHasChanges(false);
    }
    setErrors({});
  }, [event]);

  const showDiscardAlert = useCallback(async (onDiscard: () => void) => {
    if (!hasChanges) {
      onDiscard();
      return;
    }

    if (Platform.OS === 'web') {
      const confirmed = (globalThis as any).confirm?.('Discard your changes?');
      if (confirmed) {
        onDiscard();
      }
      return;
    }

    const confirmed = await showConfirm(
      'Discard Changes',
      'Are you sure you want to discard your changes?',
      'Discard',
      'Keep Editing'
    );
    
    if (confirmed) {
      onDiscard();
    }
  }, [hasChanges]);

  return {
    formData,
    errors,
    hasChanges,
    validateForm,
    handleFieldChange,
    handlePublishTypeChange,
    appendPhotos,
    resetForm,
    showDiscardAlert,
  };
};
